import type { AssignedWorksheetDetail } from '@mathflat/domain/StudentWorksheet'
import { addHours, isAfter } from 'date-fns'
import { action, computed, makeObservable, observable } from 'mobx'
import { EMPTY_NUMBER_ID } from 'src/constant/common'
import { UndoableDTO } from 'src/dto/UndoableDTO'

import { typeSafeAssign } from '@/utils/common'

type StudentWorksheetData = Omit<
  AssignedWorksheetDetail,
  'assignDatetime' | 'openDatetime' | 'scoreDatetime' | 'creverseShared'
> & {
  assignDatetime: Date
  openDatetime?: Date
  scoreDatetime?: Date
  creverseShared: boolean
}

export class StudentWorksheet extends UndoableDTO implements StudentWorksheetData {
  id = EMPTY_NUMBER_ID
  assignDatetime = new Date()
  openDatetime?: Date
  scoreDatetime?: Date
  createDatetime?: Date
  correctCount = 0
  wrongCount = 0
  score: StudentWorksheetData['score'] = null
  status = 'INCOMPLETE' as StudentWorksheetData['status']
  studentHomeworkId: StudentWorksheetData['studentHomeworkId'] = null
  essayScoringOpened: StudentWorksheetData['essayScoringOpened'] = null
  creverseShared = false
  worksheet = {
    id: EMPTY_NUMBER_ID,
    type: 'CUSTOM',
    title: '',
    titleTag: '',
    tag: 'BASIC',
    autoScorable: false,
    problemCount: 0,
    level: 1,
    school: 'ELEMENTARY',
    grade: '1',
    chapter: '',
  } as StudentWorksheetData['worksheet']

  constructor(data?: StudentWorksheetData) {
    super()
    makeObservable(this, {
      id: observable,
      assignDatetime: observable,
      scoreDatetime: observable,
      score: observable,
      status: observable,
      studentHomeworkId: observable,
      essayScoringOpened: observable,
      creverseShared: observable,
      worksheet: observable,
      canAssignHomework: computed,
      markCreverseShared: action,
    })
    if (data) typeSafeAssign(this, data)
  }

  // update() 를 쓰면 채점 낙관적 갱신의 undo 스택에 공유 스냅샷이 끼어들어 직접 대입한다
  markCreverseShared() {
    this.creverseShared = true
  }

  get isNewAssigned() {
    return isAfter(addHours(this.assignDatetime, 24), new Date())
  }

  get korWorksheetLevel() {
    switch (this.worksheet.level) {
      case 1:
        return '하'
      case 2:
        return '중하'
      case 3:
        return '중'
      case 4:
        return '상'
      case 5:
        return '최상'
    }
  }

  get assignButtonStatus() {
    switch (this.status) {
      case 'INCOMPLETE':
        return '채점'
      case 'PROGRESS':
        return '이어 채점'
      case 'COMPLETE':
        return `${this.score || 0}점`
    }
  }

  get canAssignHomework() {
    return !this.studentHomeworkId
  }
}
